import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Button, Flex } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { FundDealSummaryRes } from '@dealstack/shared/lib/generated';
import { BasicMantineTable } from '@dealstack/frontend/shared/components/BasicMantineTable/BasicMantineTable.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	getDealsForFund,
	unlinkFundFromDeal,
} from '@dealstack/frontend/shared/dataExchange/services/fund.service.ts';
import { useMemo, useState } from 'react';
import { LinkFundDrawer } from '@dealstack/frontend/app/pages/Organization/OrganizationFunds/components/LinkFundDrawer.tsx';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { getFundLinkedDealsColumns } from './fundLinkedDealsColumns';

interface Props {
	fundId: string;
	fundName: string;
	dealId: string;
}

export const FundLinkedDeals = ({ fundId, fundName, dealId }: Props) => {
	const tAdminDashboard = useTranslationAdminDashboard();

	const fundDealsQuery = useQuery({
		queryKey: ['linkedDealsForFund', { fundId }],
		queryFn: ({ signal }) => getDealsForFund({ fundId }, signal),
		enabled: !!dealId,
	});

	const fundDeals = (fundDealsQuery.data ?? []).map((fd) => fd.deal);

	const [linkDrawerOpen, setLinkDrawerOpen] = useState(false);
	const [dealToUnlink, setDealToUnlink] = useState<FundDealSummaryRes | null>(null);

	const unlinkMutation = useMutation({
		mutationFn: (variables: Parameters<typeof unlinkFundFromDeal>[0]) =>
			unlinkFundFromDeal(variables),
		onSuccess: () => {
			showToastV2({
				title: tAdminDashboard('fund_investors.toast.unlink_success.title'),
				message: tAdminDashboard('fund_investors.toast.unlink_success.message'),
			});
			setDealToUnlink(null);
			return fundDealsQuery.refetch();
		},
	});

	const columns = useMemo(
		() =>
			getFundLinkedDealsColumns(tAdminDashboard, {
				onUnlinkDeal: (deal) => setDealToUnlink(deal),
			}),
		[tAdminDashboard]
	);

	return (
		<Flex direction="column" h="100%" w="100%" p="1rem" gap="1rem">
			<Flex justify="flex-end">
				<Button
					data-testid={TEST_ELEMENT_IDS.FUND_DEALS_LINK_BUTTON}
					leftSection={<Icon variant="add" size="lg" />}
					onClick={() => setLinkDrawerOpen(true)}
				>
					{tAdminDashboard('fund_settings.table.buttons.link_deal')}
				</Button>
			</Flex>
			<Flex direction="column" w="100%" mih={0} style={{ flex: 1, overflow: 'hidden' }}>
				<BasicMantineTable<FundDealSummaryRes>
					columns={columns}
					isLoading={fundDealsQuery.isLoading}
					data={fundDeals}
					dataTestId={TEST_ELEMENT_IDS.FUND_DEALS_TABLE}
				/>
			</Flex>
			<LinkFundDrawer
				fundId={fundId}
				fundName={fundName}
				opened={linkDrawerOpen}
				onClose={() => setLinkDrawerOpen(false)}
				onSuccess={() => fundDealsQuery.refetch()}
			/>
			<ConfirmDeleteDrawer
				opened={!!dealToUnlink}
				onClose={() => setDealToUnlink(null)}
				title={tAdminDashboard('fund_settings.unlink_deal.drawer.title')}
				confirmDeleteText={dealToUnlink?.name ?? ''}
				alertDescription={tAdminDashboard(
					'fund_settings.unlink_deal.drawer.alert_description'
				)}
				inputLabel={tAdminDashboard('fund_settings.unlink_deal.drawer.inputLabel')}
				inputPlaceholder={tAdminDashboard(
					'fund_settings.unlink_deal.drawer.inputPlaceholder'
				)}
				wordMismatchErrorMessage={tAdminDashboard(
					'fund_settings.unlink_deal.drawer.wordMismatchError'
				)}
				deleteBtnText={tAdminDashboard('fund_settings.unlink_deal.drawer.confirmDelete')}
				cancelBtnText={tAdminDashboard('fund_settings.unlink_deal.drawer.cancel')}
				deleteSuccess={unlinkMutation.isSuccess}
				deleteLoading={unlinkMutation.isPending}
				handleDelete={() => {
					if (dealToUnlink && fundId) {
						unlinkMutation.mutate({
							fundId,
							dealId: dealToUnlink.id,
						});
					}
				}}
			/>
		</Flex>
	);
};
